import React, { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pushSupported, subscribeToPush, currentSubscription } from "@/lib/push";

const isIOS = typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent);
const isStandalone = typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

export default function PushOptIn() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!pushSupported()) {
        if (isIOS && !isStandalone) setShow("ios");
        return;
      }
      if (Notification.permission === "granted") {
        const s = await currentSubscription();
        if (s) { setShow(false); return; }
      }
      if (Notification.permission !== "denied") setShow("prompt");
    })();
  }, []);

  if (!show) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Push notifications enabled");
      setShow(false);
    } catch (e) {
      toast.error(e.message === "denied" ? "Notifications were blocked in your browser settings" : "Could not enable notifications");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-2xl border border-sky-200 bg-sky-50 p-4" data-testid="push-optin">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-600"><Bell className="h-5 w-5" /></span>
        <div className="flex-1">
          <p className="font-semibold text-slate-800">Get push notifications</p>
          {show === "ios" ? (
            <p className="mt-1 text-sm text-slate-600">On iPhone/iPad, open RallyPointGo in <strong>Safari</strong>, tap the <strong>Share</strong> icon, choose <strong>Add to Home Screen</strong>, then open RallyPointGo from that new icon and tap <strong>Allow</strong> when asked.</p>
          ) : (
            <>
              <p className="mt-1 text-sm text-slate-600">Turn on alerts for new events, conferences and team announcements — even when the app is closed.</p>
              <Button onClick={enable} disabled={busy} className="mt-3 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="push-enable-btn">Enable notifications</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
